import { z } from "zod";
import { OUTCOME_INCENTIVE_STATUS } from "@/other/constants/IncentiveKeys.jsx";

const filterSchema = z.object({
  field: z.string().min(1, { error: "Field is required" }),
  operator: z.string().min(1, { error: "Operator is required" }),
  value: z.coerce.number().min(1, { error: "Value is required" }),
  boolean_operator: z.string().nullable().optional()
}).passthrough();

export const outcomeIncentiveSchema = z
  .object({
    name: z.string().min(1, { error: "Name should not be empty" }),
    startDate: z.date({
      error: (issue) => issue.input === undefined
        ? "Start date is required"
        : "Please select a valid start date"
    }),
    endDate: z.date().nullable().optional(),
    points: z.preprocess((val) => {
      // Store the original value for validation
      return { original: val, processed: val };
    }, z.object({
      original: z.any(),
      processed: z.coerce.number({
        error: (issue) => issue.input === undefined
          ? "Points are required"
          : "Points must be a valid number"
      })
    }))
      .refine((data) => {
        // Check if the original string input had leading zeros
        if (typeof data.original === 'string') {
          return !(data.original.length > 1 && /^-?0\d/.test(data.original));
        }
        return true;
      }, {
        message: "Points cannot start with zero"
      })
      .refine((data) => data.processed !== 0, {
        message: "Points cannot be zero"
      })
      .refine((data) => data.processed >= -10000, {
        message: "Points value is too small"
      })
      .refine((data) => data.processed <= 10000, {
        message: "Points value is too large"
      })
      .transform((data) => data.processed),
    multiplyByDays: z.boolean().default(true),
    includeArchivedTasks: z.boolean().default(false),
    status: z.enum(OUTCOME_INCENTIVE_STATUS),
    taskFilter: z.array(filterSchema).min(1, { error: "At least one task filter is required" }),
    resourceFilter: z.array(filterSchema).optional().nullable()
  })
  .refine(
    (data) => {
      if (data.endDate && data.startDate) {
        return data.endDate > data.startDate;
      }
      return true;
    },
    {
      message: "End date must be greater than start date",
      path: ["endDate"]
    }
  ).superRefine((data, ctx) => {
    const checkDuplicates = (filters, fieldPath) => {
      const seen = new Set();

      const hasDuplicate = filters?.some((filter) => {
        const key = `${filter.field}-${filter.operator}-${filter.value}`;
        if (seen.has(key)) return true;
        seen.add(key);
        return false;
      });

      if (hasDuplicate) {
        ctx.issues.push({
          path: [fieldPath],
          code: "custom",
          message: "You have added the same filter more than once. Please adjust or remove it"
        });
      }
    };

    checkDuplicates(data.taskFilter, "taskFilter");
    if (data.resourceFilter) {
      checkDuplicates(data.resourceFilter, "resourceFilter");
    }
  });
